import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Stack, IconButton, List, ListItem, ListItemAvatar, Avatar, ListItemText, Input, Chip
} from '@mui/material';
import { Delete, Edit, Crop, Image, Preview } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
const maxSize = 5 * 1024 * 1024;

const SettingsSliderPage = () => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    const res = await fetch(`${API_BASE_URL}/api/slider-images`);
    const data = await res.json();
    setImages(Array.isArray(data) ? data.sort((a, b) => a.order - b.order) : []);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only JPG, PNG, WebP allowed.');
      return;
    }
    if (file.size > maxSize) {
      setError('File too large. Max 5MB.');
      return;
    }
    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE_URL}/api/slider-images`, {
      method: 'POST',
      body: formData,
    });
    if (res.ok) {
      await fetchImages();
    } else {
      setError('Upload failed.');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (id) => {
    await fetch(`${API_BASE_URL}/api/slider-images/${id}`, { method: 'DELETE' });
    await fetchImages();
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = Array.from(images);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setImages(reordered);
    // Update order in backend
    await fetch(`${API_BASE_URL}/api/slider-images/order`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order: reordered.map(img => img.id) }),
    });
    await fetchImages();
  };

  return (
    <Box sx={{ p: 4, bgcolor: '#f5f5f7', minHeight: '100vh' }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <Image sx={{ color: '#3730f2', fontSize: 32, mr: 1 }} /> Slider Settings
      </Typography>
      <Paper sx={{ p: 3, mb: 4, borderRadius: 4 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Upload Image</Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Button
            variant="contained"
            component="label"
            sx={{ bgcolor: '#3730f2', borderRadius: 999, fontWeight: 600 }}
            disabled={uploading}
          >
            Upload Image
            <Input
              type="file"
              inputRef={fileInputRef}
              onChange={handleUpload}
              sx={{ display: 'none' }}
              inputProps={{ accept: allowedTypes.join(',') }}
            />
          </Button>
          <Button
            variant="outlined"
            startIcon={<Preview />}
            sx={{ borderRadius: 999, fontWeight: 600, color: '#3730f2', borderColor: '#3730f2' }}
          >
            Preview Slider
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Supported formats: JPG, PNG, WebP. Maximum size: 5MB. Images are automatically saved.
        </Typography>
        {error && <Chip color="error" label={error} sx={{ mt: 2 }} />}
      </Paper>
      <Paper sx={{ p: 3, borderRadius: 4 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Drag to reorder images</Typography>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="slider-list">
            {(provided) => (
              <List ref={provided.innerRef} {...provided.droppableProps}>
                {images.map((img, idx) => (
                  <Draggable key={img.id} draggableId={img.id} index={idx}>
                    {(provided, snapshot) => (
                      <ListItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        secondaryAction={
                          <Stack direction="row" spacing={1}>
                            <IconButton sx={{ bgcolor: '#a78bfa', color: '#fff', borderRadius: '50%', p: 1 }}>
                              <Crop />
                            </IconButton>
                            <IconButton sx={{ bgcolor: '#38bdf8', color: '#fff', borderRadius: '50%', p: 1 }}>
                              <Edit />
                            </IconButton>
                            <IconButton sx={{ bgcolor: '#f87171', color: '#fff', borderRadius: '50%', p: 1 }} onClick={() => handleDelete(img.id)}>
                              <Delete />
                            </IconButton>
                          </Stack>
                        }
                        sx={{
                          bgcolor: snapshot.isDragging ? '#f3f4f6' : '#fff',
                          borderRadius: 3,
                          mb: 2,
                          boxShadow: snapshot.isDragging ? 3 : 1,
                        }}
                      >
                        <Box {...provided.dragHandleProps} sx={{ mr: 2, cursor: 'grab', color: '#a3a3a3', display: 'flex', alignItems: 'center' }}>
                          <svg width="20" height="20" viewBox="0 0 20 20"><circle cx="5" cy="6" r="1.5"/><circle cx="5" cy="10" r="1.5"/><circle cx="5" cy="14" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="15" cy="10" r="1.5"/><circle cx="15" cy="14" r="1.5"/></svg>
                        </Box>
                        <ListItemAvatar>
                          <Avatar variant="rounded" src={img.url} sx={{ width: 56, height: 56, mr: 2 }} />
                        </ListItemAvatar>
                        <ListItemText
                          primary={img.filename}
                          secondary={`Image ${idx + 1} of ${images.length}`}
                        />
                      </ListItem>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </Paper>
    </Box>
  );
};

export default SettingsSliderPage; 